// Front-end reference data for the User Access page.
//
// The backend stores a single coarse permission level per (user, property) and
// has no per-module / per-action permission storage. The descriptions and the
// capability matrix below are therefore a presentational reference only — they
// describe what each system role is intended to grant. They are not enforced
// here; server-side checks remain the source of truth.

export const ROLE_DESCRIPTIONS = {
  owner: 'Full access to all modules, including User Management and Property Settings.',
  admin: 'Administrative access across all modules, including user management and property settings.',
  manager: 'Full operational access; cannot manage users or property-level settings.',
  modify: 'Read most modules; create and edit own Tasks, Time, and Hazards.',
  worker: 'Read most modules; write own Tasks, Time, and Hazards.',
  view: 'Read access only across granted modules; no write of any kind.',
  contractor: 'Limited: own Tasks, own Time, submit Hazards / Incidents; read Map & Documents.',
};

// Display order for the roles catalog and matrix columns.
export const ROLE_RANK = {
  owner: 1,
  admin: 2,
  manager: 3,
  modify: 4,
  worker: 5,
  view: 6,
  contractor: 7,
};

export const sortRoles = (rolesArr = []) => [...rolesArr].sort(
  (a, b) => (ROLE_RANK[a.slug] || 99) - (ROLE_RANK[b.slug] || 99),
);

export const getRoleDescription = (slug) => ROLE_DESCRIPTIONS[slug]
  || 'System role. See the permission matrix for the access this role grants.';

// Capability levels used by the reference matrix.
export const CAP = {
  NONE: 'none',
  READ: 'read',
  WRITE: 'write',
  FULL: 'full',
};

export const CAP_META = {
  none: { label: '—', bg: '#f9fafb', color: '#98a2b3' },
  read: { label: 'Read', bg: '#f2f4f7', color: '#475467' },
  write: { label: 'Write', bg: '#eff8ff', color: '#175cd3' },
  full: { label: 'Full', bg: '#ecfdf3', color: '#067647' },
};

// Modules shown in the reference matrix, grouped like the design.
export const MODULE_GROUPS = [
  {
    group: 'Core',
    modules: [
      { key: 'dashboard', label: 'Dashboard / Home' },
      { key: 'map', label: 'Map' },
    ],
  },
  {
    group: 'Safety & Compliance',
    modules: [
      { key: 'hazards', label: 'Hazards' },
      { key: 'incidents', label: 'Incidents' },
      { key: 'safety', label: 'Safety (SWMS / JSA / Inductions)' },
    ],
  },
  {
    group: 'Operations',
    modules: [
      { key: 'tasks', label: 'Tasks' },
      { key: 'planner', label: 'Planner' },
      { key: 'schedule', label: 'Schedule / Rosters' },
      { key: 'time', label: 'Time & Attendance' },
    ],
  },
  {
    group: 'Administration',
    modules: [
      { key: 'documents', label: 'Documents' },
      { key: 'userManagement', label: 'User Management' },
      { key: 'propertySettings', label: 'Property Settings' },
    ],
  },
];

// Capability per role slug per module key (defaults to NONE when unspecified).
export const ROLE_CAPABILITIES = {
  owner: {
    dashboard: CAP.FULL, map: CAP.FULL, hazards: CAP.FULL, incidents: CAP.FULL,
    safety: CAP.FULL, tasks: CAP.FULL, planner: CAP.FULL, schedule: CAP.FULL,
    time: CAP.FULL, documents: CAP.FULL, userManagement: CAP.FULL, propertySettings: CAP.FULL,
  },
  admin: {
    dashboard: CAP.FULL, map: CAP.FULL, hazards: CAP.FULL, incidents: CAP.FULL,
    safety: CAP.FULL, tasks: CAP.FULL, planner: CAP.FULL, schedule: CAP.FULL,
    time: CAP.FULL, documents: CAP.FULL, userManagement: CAP.FULL, propertySettings: CAP.FULL,
  },
  manager: {
    dashboard: CAP.READ, map: CAP.FULL, hazards: CAP.FULL, incidents: CAP.FULL,
    safety: CAP.FULL, tasks: CAP.FULL, planner: CAP.FULL, schedule: CAP.FULL,
    time: CAP.FULL, documents: CAP.FULL, userManagement: CAP.NONE, propertySettings: CAP.NONE,
  },
  modify: {
    dashboard: CAP.READ, map: CAP.WRITE, hazards: CAP.WRITE, incidents: CAP.WRITE,
    safety: CAP.READ, tasks: CAP.WRITE, planner: CAP.READ, schedule: CAP.READ,
    time: CAP.WRITE, documents: CAP.READ, userManagement: CAP.NONE, propertySettings: CAP.NONE,
  },
  worker: {
    dashboard: CAP.READ, map: CAP.WRITE, hazards: CAP.WRITE, incidents: CAP.WRITE,
    safety: CAP.READ, tasks: CAP.WRITE, planner: CAP.READ, schedule: CAP.READ,
    time: CAP.WRITE, documents: CAP.READ, userManagement: CAP.NONE, propertySettings: CAP.NONE,
  },
  view: {
    dashboard: CAP.READ, map: CAP.READ, hazards: CAP.READ, incidents: CAP.READ,
    safety: CAP.READ, tasks: CAP.READ, planner: CAP.READ, schedule: CAP.READ,
    time: CAP.READ, documents: CAP.READ, userManagement: CAP.NONE, propertySettings: CAP.NONE,
  },
  contractor: {
    dashboard: CAP.READ, map: CAP.READ, hazards: CAP.WRITE, incidents: CAP.WRITE,
    safety: CAP.NONE, tasks: CAP.WRITE, planner: CAP.NONE, schedule: CAP.NONE,
    time: CAP.WRITE, documents: CAP.READ, userManagement: CAP.NONE, propertySettings: CAP.NONE,
  },
};

export const getCapability = (slug, moduleKey) => (
  (ROLE_CAPABILITIES[slug] && ROLE_CAPABILITIES[slug][moduleKey]) || CAP.NONE
);
